import React from "react";
import SEO from "../components/SEO";
import TrackingWidget from "../components/TrackingWidget";
import { Phone, Mail, MessageCircle, Search } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Standalone tracking page — Phase P1 Unified Tracking.
 *
 * Reuses the existing TrackingWidget so there is exactly one piece of
 * tracking UI to maintain. The page wraps it with proper SEO meta and
 * a tiny FAQ band so deep-linked customers can pivot to support if
 * their number is unknown.
 */
export default function TrackingPage() {
  return (
    <>
      <SEO
        title="Zending volgen — Track uw 5999Cargo trackingnummer"
        description="Vul uw trackingnummer (bijv. W523518422761 of W5999NL260089) in om de actuele status van uw zending bij 5999Cargo te bekijken. Direct, gratis, geen account nodig."
        keywords="tracking 5999Cargo, zending volgen, W-nummer, trackingnummer, cargo Curacao volgen, pakket Aruba tracking"
      />
      <main
        className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50"
        data-testid="tracking-page"
      >
        {/* Hero / intro */}
        <section className="py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#082c50]/5 border border-[#082c50]/10 text-[11px] uppercase tracking-[0.18em] font-bold text-[#082c50] mb-4">
              <Search className="w-3.5 h-3.5" />
              Tracking tool
            </div>
            <h1
              className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl text-[#082c50] tracking-tight leading-[1.05]"
              data-testid="tracking-page-title"
            >
              Zending volgen.
            </h1>
            <p className="mt-4 text-[#514e4e] text-base sm:text-lg max-w-2xl leading-relaxed">
              Vul uw trackingnummer in om de actuele status van uw zending te
              bekijken — bijvoorbeeld <code className="font-mono text-[#082c50]">W523518422761</code> of een
              W-nummer van 5999Cargo.
            </p>

            <div className="mt-10">
              <TrackingWidget />
            </div>
          </div>
        </section>

        {/* Help band */}
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-slate-200 bg-white p-7 md:p-9 shadow-sm">
              <h2 className="font-chivo font-black text-2xl text-[#082c50]">
                Trackingnummer niet gevonden?
              </h2>
              <p className="text-[#514e4e] text-sm mt-3 max-w-2xl">
                Soms duurt het enkele uren voordat een trackingnummer
                geactiveerd is in het systeem. Krijgt u na 24 uur nog steeds
                geen resultaat? Neem direct contact met ons op — wij zoeken
                het voor u uit.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a
                  href="tel:+31488235999"
                  className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-[#082c50] text-white hover:bg-[#1a5193] font-semibold text-sm transition-colors"
                  data-testid="tracking-page-call"
                >
                  <Phone className="w-4 h-4" />
                  Bel ons
                </a>
                <a
                  href="mailto:info@5999cargo.nl"
                  className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-white text-[#082c50] border border-[#082c50]/20 hover:border-[#082c50] font-semibold text-sm transition-colors"
                  data-testid="tracking-page-email"
                >
                  <Mail className="w-4 h-4" />
                  Mail ons
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-white text-[#082c50] border border-[#082c50]/20 hover:border-[#082c50] font-semibold text-sm transition-colors"
                  data-testid="tracking-page-contact"
                >
                  <MessageCircle className="w-4 h-4" />
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
